import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { EXTENSIONS } from "@/lib/extensions";
import BaseProfileForm from "@/components/BaseProfileForm";
import { toast } from "sonner";
import { ArrowRight, Check, Search, Tag, Ship, Sailboat } from "lucide-react";

const ICONS = { buyer: Search, seller: Tag, owner: Ship, renter: Sailboat };

export default function Onboarding() {
  const { t, lang } = useLang();
  const a = t.account;
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [base, setBase] = useState(null);
  const [intents, setIntents] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/profile/base").then((r) => setBase(r.data)); }, []);

  const toggle = (k) => setIntents((v) => (v.includes(k) ? v.filter((x) => x !== k) : [...v, k]));

  const finish = async () => {
    setBusy(true);
    try {
      await api.post("/profile/intents", { intents });
      await refreshMe();
      toast.success(a.saved);
      navigate(intents.length ? `/perfil/extension/${intents[0]}` : "/dashboard");
    } catch { toast.error("Error"); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-16 lg:py-24" data-testid="onboarding-page">
      <div className="flex items-center gap-3 text-xs font-display tracking-[0.2em] uppercase text-slate-400" data-testid="onboarding-progress">
        <span className={step === 1 ? "text-[#0B192C]" : ""}>1 · {a.baseProfile}</span>
        <span className="h-px w-8 bg-slate-200" />
        <span className={step === 2 ? "text-[#0B192C]" : ""}>2 · {a.intentsTitle}</span>
      </div>

      {step === 1 && (
        <>
          <h1 className="font-serif text-4xl lg:text-5xl mt-6 text-[#0B192C]" data-testid="onboarding-step1-title">{a.onboardingTitle}</h1>
          <p className="mt-3 text-slate-500 max-w-xl">{a.onboardingSubtitle}</p>
          <div className="mt-12">
            {base ? <BaseProfileForm base={base} submitLabel={t.onboarding.continue} onSaved={() => setStep(2)} testid="onb-base" /> : <div className="h-64 bg-slate-50 animate-pulse rounded-2xl" />}
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <h1 className="font-serif text-4xl lg:text-5xl mt-6 text-[#0B192C]" data-testid="onboarding-step2-title">{a.intentsTitle}</h1>
          <p className="mt-3 text-slate-500 max-w-xl">{a.intentsSubtitle}</p>
          <div className="mt-12 grid sm:grid-cols-2 gap-5" data-testid="onboarding-intents">
            {Object.entries(EXTENSIONS).map(([k, ext]) => {
              const Icon = ICONS[k];
              const on = intents.includes(k);
              return (
                <button key={k} type="button" onClick={() => toggle(k)} data-testid={`onboarding-intent-${k}`}
                  className={`text-left rounded-2xl border p-6 transition-colors ${on ? "border-[#0B192C] bg-[#0B192C] text-white" : "border-slate-200 hover:border-slate-400 bg-white"}`}>
                  <div className="flex items-center justify-between">
                    <Icon className={`h-6 w-6 ${on ? "text-[#C2A878]" : "text-[#0B192C]"}`} strokeWidth={1.6} />
                    {on && <Check className="h-4 w-4" />}
                  </div>
                  <h3 className={`font-serif text-2xl mt-6 ${on ? "text-white" : "text-[#0B192C]"}`}>{ext.title[lang]}</h3>
                  <p className={`mt-2 text-sm ${on ? "text-white/80" : "text-slate-500"}`}>{ext.desc[lang]}</p>
                </button>
              );
            })}
          </div>
          <div className="mt-12 flex items-center justify-between">
            <button onClick={() => setStep(1)} className="btn-ghost text-sm" data-testid="onboarding-back">{a.back}</button>
            <button disabled={busy} onClick={finish} className="btn-primary disabled:opacity-40" data-testid="onboarding-continue">
              {intents.length ? t.onboarding.continue : a.exploreMode} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
